import { Injectable } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { AppForm } from '../../../../../../common/forms/app.form';
import { ApplicationAppEntityApplicationRead, ApplicationAppEntityApplicationWrite } from '../../../../../../openapi';
import { environment } from '../../../../../../../environments/environment';
import { CustomValidator } from '../../../../../../common/forms/custom.validators';
import { ManagerForm } from './manager.form';

export type TransitPeriodItem = '1 miesiąc' | '6 miesięcy' | '12 miesięcy' | '24 miesięcy' | string;

export type DeviceType = 'Urządzenie obce' | 'Reklama' | string;

@Injectable()
export class ApplicationDataForm extends AppForm {

  public readonly Validators = Validators;

  public transitPeriods: TransitPeriodItem[] = [
    '1 miesiąc',
    '6 miesięcy',
    '12 miesięcy',
    '24 miesięcy'
  ];

  public deviceTypes: DeviceType[] = [
    'Urządzenie niezwiązane z funkcjonowaniem drogi',
    'Reklama'
  ];

  public exitClasses: string[] = [
    'A',
    'B',
    'C1',
    'C2',
    'D',
    'E'
  ];

  constructor() {
    super({
      office: new FormControl({ value: environment.applicationSubmittedTo, disabled: true }, { nonNullable: true }),
      documentReceiptMethod: new FormControl('', { validators: [Validators.required], nonNullable: true })
    });
  }

  public createRoadOccupationPurposeControl(): void {
    this.addControl('roadOccupationPurpose', new FormControl(null, [Validators.required]));
  }

  public get roadOccupationPurpose(): FormControl | null {
    return this.get('roadOccupationPurpose') as FormControl | null;
  }

  public roadOccupationPurposeChange(value: string): void {
    switch (value) {
      // przebudowa zjazdu
      case 'bfe680d8-a2d0-4d60-a584-2c231c603691': {
        this.agreementDate?.setValidators([]);
        this.agreementNumber?.setValidators([]);
        break;
      }
      // kategorii 2
      case '5f1a152d-2547-4827-b2f2-0fdd97e69c58': {
        this.addControl(
          'transitPeriod',
          new FormControl('', {
            nonNullable: true,
            validators: [Validators.required, Validators.maxLength(255)]
          })
        );
        break;
      }
      default: {
        this.agreementNumber?.setValidators([Validators.required, Validators.maxLength(255)]);
        this.agreementDate?.setValidators([Validators.required, CustomValidator.maxDate(new Date())]);

        this.removeControl('transitPeriod');
        break;
      }
    }
    this.agreementDate?.updateValueAndValidity();
    this.agreementNumber?.updateValueAndValidity();
  }

  public createExitClassControl(): void {
    this.addControl('exitClass', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get exitClass(): FormControl | null {
    return this.get('exitClass') as FormControl | null;
  }

  public get objectName(): FormControl | null {
    return this.get('objectName') as FormControl | null;
  }

  public createObjectNameControl(): void {
    this.addControl('objectName', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
  }

  public get roadTrafficImpact(): FormControl | null {
    return this.get('roadTrafficImpact') as FormControl | null;
  }

  public createRoadTrafficImpactControl(): void {
    this.addControl('roadTrafficImpact', new FormControl('Nie', Validators.required));
  }

  public get agreementNumber(): FormControl | null {
    return this.get('agreementNumber') as FormControl | null;
  }

  public get agreementDate(): FormControl | null {
    return this.get('agreementDate') as FormControl | null;
  }

  public createAgreementControls(): void {
    this.addControl('agreementNumber', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
    this.addControl('agreementDate', new FormControl(null, [Validators.required, CustomValidator.maxDate(new Date())]));
  }

  public get documentReceiptMethod(): FormControl {
    return this.get('documentReceiptMethod') as FormControl;
  }

  public get ePuapAddress(): FormControl | null {
    return this.get('ePuapAddress') as FormControl | null;
  }

  public get eDeliveryAddress(): FormControl | null {
    return this.get('eDeliveryAddress') as FormControl | null;
  }

  public documentReceiptMethodChange(v: string): void {
    if (v === '4d536cdd-dc3a-42f9-bf89-342e5cc0c885') {
      this.addControl(
        'ePuapAddress',
        new FormControl('', [Validators.required, Validators.maxLength(255)])
      );
    } else {
      this.removeControl('ePuapAddress');
    }
    if (v === '6bef93e4-f125-49f2-ab12-11ddc5e1e8a2') {
      this.addControl('eDeliveryAddress', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    } else {
      this.removeControl('eDeliveryAddress');
    }
  }

  public get transitPeriod(): FormControl | null {
    return this.get('transitPeriod') as FormControl | null;
  }

  public createApplicationConcernsStreetControl(): void {
    this.addControl('applicationConcernsStreet', new FormControl(null, { validators: [Validators.required, Validators.maxLength(255)] }));
  }

  public get applicationConcernsStreet(): FormControl | null {
    return this.get('applicationConcernsStreet') as FormControl | null;
  }

  public get applicationSubject(): FormControl | null {
    return this.get('applicationSubject') as FormControl | null;
  }

  public showResponsiblePersonControls(): boolean {
    return !!this.responsiblePersonPhone && !!this.responsiblePersonLastName && !!this.responsiblePersonFirstName && !!this.responsiblePersonEmail;
  }

  private get responsiblePersonFirstName(): FormControl | null {
    return this.get('responsiblePersonFirstName') as FormControl | null;
  }

  private get responsiblePersonLastName(): FormControl | null {
    return this.get('responsiblePersonLastName') as FormControl | null;
  }

  private get responsiblePersonPhone(): FormControl | null {
    return this.get('responsiblePersonPhone') as FormControl | null;
  }

  private get responsiblePersonEmail(): FormControl | null {
    return this.get('responsiblePersonEmail') as FormControl | null;
  }

  public createResponsiblePersonControls(): void {
    this.addControl('responsiblePersonFirstName', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    this.addControl('responsiblePersonLastName', new FormControl('', [Validators.required, Validators.maxLength(255)]));
    this.addControl('responsiblePersonPhone', new FormControl(''));
    this.addControl('responsiblePersonEmail', new FormControl('', [Validators.email, Validators.maxLength(255)]));
    this.addControl('responsiblePersonEmail', new FormControl('', [Validators.required, Validators.maxLength(255)]));
  }

  public createManagerForm(): void {
    this.addControl('managerForm', new ManagerForm());
  }

  public get managerForm(): ManagerForm | null {
    return this.get('managerForm') as ManagerForm | null;
  }

  public get comments(): FormControl | null {
    return this.get('comments') as FormControl | null;
  }

  public createCommentsControl(): void {
    this.addControl('comments', new FormControl('', [Validators.maxLength(5000)]));
  }

  public createDeviceTypeControl(): void {
    this.addControl('deviceType', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get deviceType(): FormControl | null {
    return this.get('deviceType') as FormControl | null;
  }

  public createDeviceNameControl(): void {
    this.addControl('deviceName', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get deviceName(): FormControl | null {
    return this.get('deviceName') as FormControl | null;
  }

  public deviceTypeChange(v: DeviceType): void {
    if (v === 'Urządzenie obce') {
      this.addControl('deviceTypeDescription', new FormControl(null, [Validators.required, Validators.maxLength(5000)]));
    } else {
      this.removeControl('deviceTypeDescription');
    }
  }

  public get deviceTypeDescription(): FormControl | null {
    return this.get('deviceTypeDescription') as FormControl | null;
  }

  public createCorrespondenceControls(): void {
    this.addControl('correspondenceFirstName', new FormControl(null, [Validators.maxLength(255)]));
    this.addControl('correspondenceLastName', new FormControl(null, [Validators.maxLength(255)]));
    this.addControl('correspondencePhone', new FormControl(null, [CustomValidator.phone(), Validators.maxLength(255)]));
  }

  public get showCorrespondenceControls(): boolean {
    return !!this.correspondencePhone
      || !!this.correspondenceLastName
      || !!this.correspondenceFirstName;
  }

  public get correspondenceFirstName(): FormControl | null {
    return this.get('correspondenceFirstName') as FormControl | null;
  }

  public get correspondenceLastName(): FormControl | null {
    return this.get('correspondenceLastName') as FormControl | null;
  }

  public get correspondencePhone(): FormControl | null {
    return this.get('correspondencePhone') as FormControl | null;
  }

  public createLegalInterestControl(): void {
    this.addControl('legalInterest', new FormControl('', [Validators.required, Validators.max(5000)]));
  }

  public get legalInterest(): FormControl | null {
    return this.get('legalInterest') as FormControl | null;
  }

  public toWriteModel(): ApplicationAppEntityApplicationWrite {
    return {
      roadOccupationPurpose: this.roadOccupationPurpose?.value || null,
      transitPeriod: this.transitPeriod?.value || null,
      exitClass: this.value?.exitClass || null,
      documentReceiptMethod: this.value.documentReceiptMethod,
      responsiblePersonFirstName: this.value?.responsiblePersonFirstName,
      responsiblePersonLastName: this.value?.responsiblePersonLastName,
      responsiblePersonPhone: this.value?.responsiblePersonPhone,
      responsiblePersonEmail: this.value?.responsiblePersonEmail,
      managerEmail: this.value?.managerEmail,
      managerPhone: this.value?.managerPhone,
      managerFirstName: this.value?.managerFirstName,
      managerLastName: this.value?.managerLastName,
      notes: this.value?.comments,
      deviceTypeDescription: this.value?.deviceTypeDescription,
      ePuapAddress: this.ePuapAddress?.value || null,
      eDeliveryAddress: this.eDeliveryAddress?.value || null,
      applicationConcernsStreet: this.applicationConcernsStreet?.value || null,
      deviceType: this.deviceType?.value || null,
      deviceName: this.deviceName?.value || null,
      agreementNumber: this.agreementNumber?.value,
      agreementDate: this.agreementDate?.value,
      objectName: this.objectName?.value || null,
      roadTrafficImpact: this.roadTrafficImpact?.value || null,
      legalInterest: this.legalInterest?.value,

      correspondenceFirstName: this.value?.correspondenceFirstName,
      correspondenceLastName: this.value?.correspondenceLastName,
      correspondencePhone: this.value?.correspondencePhone,

      ...this.managerForm?.value
    };
  }

  public override patchFormValues(app: ApplicationAppEntityApplicationRead): void {
    this.roadOccupationPurposeChange(app?.roadOccupationPurpose?.id || '');

    if ((app?.service?.roadOccupationPurposes?.length || 0) > 1) {
      this.createRoadOccupationPurposeControl();
    } else {
      this.removeControl('roadOccupationPurpose');
    }

    this.patchValue({
      roadOccupationPurpose: app?.roadOccupationPurpose?.id || '',
      documentReceiptMethod: app?.documentReceiptMethod?.id || '',
      transitPeriod: app?.transitPeriod || '',
      exitClass: app?.exitClass || '',
      responsiblePersonFirstName: app?.responsiblePersonFirstName,
      responsiblePersonLastName: app?.responsiblePersonLastName,
      responsiblePersonPhone: app?.responsiblePersonPhone,
      responsiblePersonEmail: app?.responsiblePersonEmail,

      comments: app?.notes,
      deviceType: app?.deviceType || '',
      legalInterest: app?.legalInterest,
      actType: app?.actType || '',
      applicationConcernsStreet: app?.applicationConcernsStreet,
      agreementNumber: app?.agreementNumber,
      agreementDate: app?.agreementDate,
      objectName: app?.objectName,
      roadTrafficImpact: app?.roadTrafficImpact || 'Nie',
      deviceName: app?.deviceName || '',

      correspondenceFirstName: app?.correspondenceFirstName,
      correspondenceLastName: app?.correspondenceLastName,
      correspondencePhone: app?.correspondencePhone
    });

    this.managerForm?.patchValue(app);

    this.deviceTypeChange(app?.deviceType || '');

    this.deviceTypeDescription?.setValue(app?.deviceTypeDescription);

    this.documentReceiptMethodChange(app?.documentReceiptMethod?.id || '');
    this.ePuapAddress?.setValue(app?.ePuapAddress || '');
    this.eDeliveryAddress?.setValue(app?.eDeliveryAddress);
  }
}
